import { useClaimsConstants, usePolicyDetail } from "data/ss-data-hooks";
import SettingsPolicy from "pages/settings/common/settings-policy";
import { EditContactAddressButtonFormik } from "pages/settings/contact/contact-item/edit-contact-address-button-formik";
import { EditContactEmailButton } from "pages/settings/contact/contact-item/edit-contact-email-button";
import { EditContactPhoneButton } from "pages/settings/contact/contact-item/edit-contact-phone-button";
import { type Address, formatAddress, getPaperlessEnrollment } from "utils";

import styles from "./contact-item.module.scss";

type ContactItemProps = {
  policyDetails: {
    policyId: string;
    propertyAddress: Address;
    isCommercial: boolean;
  };
  userEmail: string;
};

/**
 * Mutliple data sources in this component.
 * - `policyDetails` - passed in from the user's policy list
 * - `userEmail` - logged in user's mss account email
 * - `fullDetails` - full policy where we find the insured email, billing
 *    information, and mailing address
 * - `claimsConstants` - to get the insured's phone number
 */
export const ContactItem = ({ policyDetails, userEmail }: ContactItemProps) => {
  const { policyId, propertyAddress, isCommercial } = policyDetails;
  const {
    data: claimsConstants,
    isLoading: phoneLoading,
    mutate: refreshPhone,
  } = useClaimsConstants(policyId);
  const {
    data: fullDetails,
    isLoading: policyLoading,
    mutate: refreshPolicy,
  } = usePolicyDetail(policyId);

  let isEnrolledBilling = false;
  let isEnrolledDocuments = false;
  if (fullDetails?.userDetails && fullDetails?.userBilling) {
    ({ isEnrolledBilling, isEnrolledDocuments } = getPaperlessEnrollment(
      fullDetails.userBilling,
      fullDetails.userDetails
    ));
  }

  const displayEmail = fullDetails?.userDetails?.primaryInsured?.emailAddress;
  const displayPhone = claimsConstants?.PolicyHolderDetails.phoneNumber;
  const displayAddress = fullDetails?.userDetails?.mailingAddress
    ? formatAddress(fullDetails?.userDetails.mailingAddress, true)
    : "";

  // This is gross.
  // Remove this when we allow editing Vave/Markel contact information
  // We can remove `userPolicyProperties from the contactItemData query as well
  const NON_EDITING_CARRIERS = ["vave", "markel"];
  const allowEditing = !NON_EDITING_CARRIERS.includes(
    fullDetails?.userPolicyProperties?.carrierGroup?.toLowerCase() ?? ""
  );

  return (
    <SettingsPolicy
      isCommercial={!!isCommercial}
      address={propertyAddress}
      detailItems={[
        { label: "Policy number:", value: policyId },
        {
          label: "Insured email:",
          value: (
            <div className={styles.info}>
              {displayEmail}
              {allowEditing ? (
                <EditContactEmailButton
                  policyId={policyId}
                  userEmail={userEmail}
                  isEnrolledBilling={isEnrolledBilling}
                  isEnrolledDocuments={isEnrolledDocuments}
                  onSuccess={() => {
                    refreshPolicy();
                  }}
                />
              ) : null}
            </div>
          ),
        },
        {
          label: "Phone:",
          value: (
            <div className={styles.info}>
              {displayPhone}
              {allowEditing ? (
                <EditContactPhoneButton
                  policyId={policyId}
                  userEmail={userEmail}
                  onSuccess={() => {
                    refreshPhone();
                  }}
                />
              ) : null}
            </div>
          ),
        },
        {
          label: "Mailing address:",
          value: (
            <div className={styles.info}>
              {displayAddress}
              {allowEditing ? (
                <EditContactAddressButtonFormik
                  policyId={policyId}
                  userEmail={userEmail}
                  onSuccess={() => {
                    refreshPolicy();
                  }}
                />
              ) : null}
            </div>
          ),
        },
      ]}
      loading={phoneLoading || policyLoading || !policyId}
    />
  );
};
